import { FC, useEffect, useState } from "react";
import SettingTitle from "../component/SettingTitle";
import { Box, Typography } from "@mui/material";
import theme from "@/theme";
import EmptySquareIcon from "@/assets/svg/EmptySquareIcon";
import BlueCheckedSquareIcon from "@/assets/svg/BlueCheckedSquareIcon";
import { useGetUserSettingsQuery, useUpdateUserSettingsMutation } from "@/store/api/authApi";
import CustomToast from "@/components/UI/CustomToast/CustomToast";

interface NotificationSettings {
  muteUnfollowedUsers: boolean;
  muteNonFollowers: boolean;
  muteNewAccounts: boolean;
  muteDefaultPhotos: boolean;
  muteUnverifiedEmails: boolean;
}

type SettingKey = keyof NotificationSettings;

interface SettingItem {
  key: SettingKey;
  label: string;
  description?: string;
}

interface INotificationProps {}

const ManageNotifications: FC<INotificationProps> = ({}) => {
  const [toast, setToast] = useState<{ message: string; success: boolean } | null>(null);

  const { data: settings } = useGetUserSettingsQuery();
  const [updateSettings] = useUpdateUserSettingsMutation();

  const [localSettings, setLocalSettings] = useState<NotificationSettings>({
    muteUnfollowedUsers: false,
    muteNonFollowers: false,
    muteNewAccounts: false,
    muteDefaultPhotos: false,
    muteUnverifiedEmails: false,
  });

  useEffect(() => {
    if (settings) {
      setLocalSettings({
        muteUnfollowedUsers: settings.muteUnfollowedUsers ?? false,
        muteNonFollowers: settings.muteNonFollowers ?? false,
        muteNewAccounts: settings.muteNewAccounts ?? false,
        muteDefaultPhotos: settings.muteDefaultPhotos ?? false,
        muteUnverifiedEmails: settings.muteUnverifiedEmails ?? false,
      });
    }
  }, [settings]);

  const handleToggle = async (key: SettingKey) => {
    const newValue = !localSettings[key];

    setLocalSettings((prev) => ({
      ...prev,
      [key]: newValue,
    }));

    try {
      await updateSettings({ [key]: newValue }).unwrap();

      setToast({
        message: `Setting "${key}" has been ${newValue ? "enabled" : "disabled"}.`,
        success: true,
      });
    } catch (error) {
      setLocalSettings((prev) => ({
        ...prev,
        [key]: !newValue,
      }));
      setToast({
        message: `Something went wrong. Please try again.`,
        success: false,
      });
      console.error("Failed to update setting:", error);
    }
  };

  const settingItems: SettingItem[] = [
    {
      key: "muteUnfollowedUsers",
      label: "You don’t follow",
    },
    {
      key: "muteNonFollowers",
      label: "Who don’t follow you",
    },
    {
      key: "muteNewAccounts",
      label: "With a new account",
    },
    {
      key: "muteDefaultPhotos",
      label: "Who have a default profile photo",
    },
    {
      key: "muteUnverifiedEmails",
      label: "Who haven’t confirmed their email",
    },
  ];

  return (
    <>
      {toast && (
        <CustomToast
          message={toast.message}
          success={toast.success}
          onClose={() => setToast(null)}
          durationMs={2000}
        />
      )}
      <SettingTitle title="Manage Notifications" back isActive={true} />
      <Box
        component="form"
        sx={{
          backgroundColor: "white",
          padding: "12px 16px",
          borderBottomLeftRadius: "1rem",
          borderBottomRightRadius: "1rem",
        }}
      >
        <Typography
          color={theme.palette.custom.blueGray900}
          fontWeight={500}
          lineHeight={"22px"}
          fontSize={"16px"}
          letterSpacing={0}
        >
          Mute notifications from people:
        </Typography>
        <Box sx={{ mt: "10px", display: "flex", flexDirection: "column", gap: "12px" }}>
          {settingItems.map((item, index) => (
            <Box
              key={index}
              sx={{
                height: "50px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography
                color={theme.palette.custom.blueGray900}
                fontWeight={500}
                lineHeight={"22px"}
                fontSize={"14px"}
                letterSpacing={0}
              >
                {item.label}
              </Typography>
              <Box
                role="button"
                aria-label={localSettings[item.key] ? "Enabled" : "Disabled"}
                tabIndex={0}
                onClick={() => handleToggle(item.key)}
                sx={{
                  width: 24,
                  height: 24,
                  display: "grid",
                  placeItems: "center",
                  cursor: "pointer",
                  userSelect: "none",
                }}
              >
                {localSettings[item.key] ? <BlueCheckedSquareIcon /> : <EmptySquareIcon />}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </>
  );
};

export default ManageNotifications;
